// src/components/Teacher/TeacherClasses.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    collection, query, where, getDocs,
    doc, updateDoc, deleteDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import ClassCreationModal from './ClassCreationModal';
import ModerationInbox from './ModerationInbox';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceHighest:     '#cfdef5',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface ClassData {
    id: string;
    name: string;
    joinCode: string;
    teacherId: string;
    students: { [key: string]: boolean };
    archived?: boolean;
    createdAt?: any;
}
 
// ── Main component ─────────────────────────────────────────────────────────────
const TeacherClasses: React.FC = () => {
    const navigate        = useNavigate();
    const { currentUser } = useAuth();
 
    const [classes, setClasses]           = useState<ClassData[]>([]);
    const [loading, setLoading]           = useState(true);
    const [showModal, setShowModal]       = useState(false);
    const [confirmAction, setConfirmAction] = useState<{ type: 'archive' | 'delete'; cls: ClassData } | null>(null);
    const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
    const [copiedId, setCopiedId]         = useState<string | null>(null);
    const [filterArchived, setFilterArchived] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    const load = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const snap = await getDocs(
                query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid))
            );
            const loaded: ClassData[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as ClassData));
            loaded.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
            setClasses(loaded);
        } catch (err) {
            console.error('Error loading classes:', err);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);
 
    useEffect(() => { load(); }, [load]);
 
    const handleCopy = (cls: ClassData) => {
        navigator.clipboard.writeText(cls.joinCode);
        setCopiedId(cls.id);
        setTimeout(() => setCopiedId(null), 2000);
    };
 
    const handleArchive = async (cls: ClassData) => {
        const newArchived = !cls.archived;
        await updateDoc(doc(db, 'classes', cls.id), { archived: newArchived });
        setClasses(prev => prev.map(c => c.id === cls.id ? { ...c, archived: newArchived } : c));
        setConfirmAction(null);
    };
 
    const handleDelete = async (cls: ClassData) => {
        await deleteDoc(doc(db, 'classes', cls.id));
        setClasses(prev => prev.filter(c => c.id !== cls.id));
        setConfirmAction(null);
    };
 
    const filtered = classes.filter(c => filterArchived ? c.archived : !c.archived);
    const activeCount   = classes.filter(c => !c.archived).length;
    const archivedCount = classes.filter(c => c.archived).length;
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginBottom: '32px', flexWrap: 'wrap' }}>
                    <div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={{ background: 'none', border: 'none', color: C.onSurfaceVariant, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            ← Dashboard
                        </button>
                        <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.8rem' : '2.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 4px 0' }}>
                            My Classes
                        </h1>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
                            {activeCount} active {activeCount !== 1 ? 'classes' : 'class'}
                            {archivedCount > 0 && ` · ${archivedCount} archived`}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '12px 24px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}40`, whiteSpace: 'nowrap', transition: 'opacity 0.15s, transform 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        ➕ New Class
                    </button>
                </div>

                <ModerationInbox onCountChange={setPendingCount} />
 
                {/* ── Filter pills ─────────────────────────────────────────── */}
                {archivedCount > 0 && (
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                        {[
                            { label: `Active (${activeCount})`,     value: false },
                            { label: `Archived (${archivedCount})`, value: true },
                        ].map(opt => (
                            <button
                                key={String(opt.value)}
                                onClick={() => setFilterArchived(opt.value)}
                                style={{ padding: '8px 18px', borderRadius: '9999px', border: 'none', background: filterArchived === opt.value ? C.primary : C.surfaceHigh, color: filterArchived === opt.value ? '#fff' : C.onSurfaceVariant, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
 
                {/* ── Class list ───────────────────────────────────────────── */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: C.onSurfaceVariant }}>Loading classes...</div>
                ) : filtered.length === 0 ? (
                    <div style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '64px 32px', textAlign: 'center', border: `1px solid ${C.surfaceHigh}` }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🏫</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.3rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {filterArchived ? 'No archived classes' : 'No classes yet'}
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 24px 0' }}>
                            {filterArchived ? 'Archived classes will appear here.' : 'Create your first class and invite your students!'}
                        </p>
                        {!filterArchived && (
                            <button
                                onClick={() => setShowModal(true)}
                                style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', padding: '13px 28px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Create First Class
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                        {filtered.map((cls, i) => {
                            const studentCount = Object.keys(cls.students || {}).length;
                            const isFirst = i === 0 && !filterArchived;
                            return (
                                <div
                                    key={cls.id}
                                    style={{
                                        background: isFirst
                                            ? `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`
                                            : C.surfaceLowest,
                                        borderRadius: '20px',
                                        padding: '24px',
                                        border: isFirst ? 'none' : `1px solid ${C.surfaceHigh}`,
                                        boxShadow: isFirst ? `0 8px 24px ${C.primary}30` : '0 2px 8px rgba(0,0,0,0.04)',
                                        display: 'flex', flexDirection: 'column', gap: '16px',
                                        opacity: cls.archived ? 0.7 : 1,
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                    }}
                                    onMouseEnter={e => { if (!isFirst) { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; } }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = isFirst ? `0 8px 24px ${C.primary}30` : '0 2px 8px rgba(0,0,0,0.04)'; }}
                                >
                                    {/* Class name + student count */}
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                                            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.15rem', color: isFirst ? '#fff' : C.onBackground, margin: 0, lineHeight: '1.3' }}>
                                                {cls.name}
                                            </h3>
                                            {cls.archived && (
                                                <span style={{ background: C.surfaceHighest, color: C.onSurfaceVariant, padding: '2px 8px', borderRadius: '9999px', fontSize: '0.62rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                                                    Archived
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: isFirst ? 'rgba(255,255,255,0.75)' : C.onSurfaceVariant, fontWeight: '500' }}>
                                            👥 {studentCount} student{studentCount !== 1 ? 's' : ''} enrolled
                                        </div>
                                    </div>
 
                                    {/* Join code */}
                                    <div style={{ background: isFirst ? 'rgba(255,255,255,0.15)' : C.surfaceLow, borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                        <div>
                                            <div style={{ fontSize: '0.65rem', fontWeight: '800', color: isFirst ? 'rgba(255,255,255,0.6)' : C.onSurfaceVariant, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Join Code</div>
                                            <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: '900', letterSpacing: '2px', color: isFirst ? '#fff' : C.primary }}>
                                                {cls.joinCode}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleCopy(cls)}
                                            style={{ background: isFirst ? 'rgba(255,255,255,0.2)' : C.primaryContainer, color: isFirst ? '#fff' : C.primary, border: 'none', borderRadius: '9999px', padding: '6px 14px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                        >
                                            {copiedId === cls.id ? '✅ Copied!' : '📋 Copy'}
                                        </button>
                                    </div>
 
                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {!cls.archived && (
                                            <button
                                                onClick={() => navigate(`/teacher/class/${cls.id}`)}
                                                style={{ flex: 1, background: isFirst ? 'rgba(255,255,255,0.9)' : C.primary, color: isFirst ? C.primary : '#fff', border: 'none', borderRadius: '9999px', padding: '10px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                                                onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                            >
                                                Manage Class →
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setConfirmAction({ type: 'archive', cls })}
                                            style={{ background: isFirst ? 'rgba(255,255,255,0.15)' : C.surfaceLow, color: isFirst ? '#fff' : C.onSurfaceVariant, border: 'none', borderRadius: '9999px', padding: '10px 14px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                            onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                                            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                        >
                                            {cls.archived ? '📤 Restore' : '📥 Archive'}
                                        </button>
                                        <button
                                            onClick={() => setConfirmAction({ type: 'delete', cls })}
                                            style={{ background: isFirst ? 'rgba(255,255,255,0.15)' : C.errorContainer, color: isFirst ? '#fff' : C.error, border: 'none', borderRadius: '9999px', padding: '10px 14px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                            onMouseEnter={e => { if (!isFirst) { e.currentTarget.style.background = C.error; e.currentTarget.style.color = '#fff'; } else { e.currentTarget.style.opacity = '0.7'; } }}
                                            onMouseLeave={e => { if (!isFirst) { e.currentTarget.style.background = C.errorContainer; e.currentTarget.style.color = C.error; } else { e.currentTarget.style.opacity = '1'; } }}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
 
                        {/* Add new class card */}
                        <button
                            onClick={() => setShowModal(true)}
                            style={{ background: 'transparent', border: `2px dashed ${C.outlineVariant}`, borderRadius: '20px', padding: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer', transition: 'all 0.15s', minHeight: '160px' }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.surfaceLow; e.currentTarget.style.borderColor = C.primary; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.outlineVariant; }}
                        >
                            <span style={{ fontSize: '2rem' }}>➕</span>
                            <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '700', fontSize: '0.875rem', color: C.onSurfaceVariant }}>Add New Class</span>
                        </button>
                    </div>
                )}
            </div>
 
            {/* ── Confirm modal ────────────────────────────────────────────── */}
            {confirmAction && (
                <div
                    onClick={() => setConfirmAction(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '32px', maxWidth: '400px', width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                        <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>
                            {confirmAction.type === 'archive' ? (confirmAction.cls.archived ? '📤' : '📥') : '🗑️'}
                        </div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 10px 0' }}>
                            {confirmAction.type === 'archive'
                                ? (confirmAction.cls.archived ? 'Restore this class?' : 'Archive this class?')
                                : 'Delete this class?'}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 8px 0', lineHeight: '1.6' }}>
                            <strong style={{ color: C.onBackground }}>{confirmAction.cls.name}</strong>
                        </p>
                        <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: '0 0 24px 0', lineHeight: '1.6' }}>
                            {confirmAction.type === 'archive'
                                ? (confirmAction.cls.archived
                                    ? 'Students will regain access and the class will appear as active.'
                                    : 'Students will lose access but their data will be preserved. You can restore it later.')
                                : 'This will permanently delete the class and all its data. This cannot be undone.'}
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setConfirmAction(null)}
                                style={{ flex: 1, padding: '12px', background: C.surfaceLowest, color: C.onSurfaceVariant, border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => confirmAction.type === 'archive' ? handleArchive(confirmAction.cls) : handleDelete(confirmAction.cls)}
                                style={{ flex: 1, padding: '12px', background: confirmAction.type === 'delete' ? C.error : C.primary, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                {confirmAction.type === 'archive'
                                    ? (confirmAction.cls.archived ? 'Yes, Restore' : 'Yes, Archive')
                                    : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
 
            {/* ── Class creation modal ─────────────────────────────────────── */}
            {showModal && (
                <ClassCreationModal
                    onClose={() => setShowModal(false)}
                    onClassCreated={() => { setShowModal(false); load(); }}
                />
            )}
        </div>
    );
};
 
export default TeacherClasses;